import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001',
  withCredentials: true,   // send httpOnly cookie on every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor — redirect to login on 401, except for:
//  /auth/me   — session check on app load; AuthContext handles the 401 itself
//  /staff/me  — optional permission check; 401 just means "not staff", not "logged out"
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes('/auth/me') &&
      !error.config?.url?.includes('/staff/me')
    ) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Tournament API
export const tournamentApi = {
  getAll: (params) => api.get('/api/tournaments', { params }),
  getById: (id) => api.get(`/api/tournaments/${id}`),
  create: (data) => api.post('/api/tournaments', data),
  update: (id, data) => api.put(`/api/tournaments/${id}`, data),
  delete: (id) => api.delete(`/api/tournaments/${id}`),
  
  // Players
  getPlayers: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/players`),
  registerPlayer: (tournamentId, data) => api.post(`/api/tournaments/${tournamentId}/players`, data),
  
  // Organizer - add player directly
  addPlayer: (tournamentId, data) => api.post(`/api/tournaments/${tournamentId}/players/add`, data),

  // Organizer - bulk add players from Excel
  addPlayersBulk: (tournamentId, formData) => api.post(`/api/tournaments/${tournamentId}/players/bulk`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Organizer - preview bulk import (2-phase upload)
  previewBulkPlayers: (tournamentId, formData) => api.post(`/api/tournaments/${tournamentId}/players/preview-bulk`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Organizer - delete player
  deletePlayer: (tournamentId, playerId) => api.delete(`/api/tournaments/${tournamentId}/players/${playerId}`),

  // Organizer - bulk delete players
  deletePlayers: (tournamentId, ids) => api.delete(`/api/tournaments/${tournamentId}/players/bulk`, { data: { ids } }),

  // Organizer - update player
  updatePlayer: (tournamentId, playerId, data) => api.put(`/api/tournaments/${tournamentId}/players/${playerId}`, data),
  
  // Organizer - generate dummy players
  generateDummyPlayers: (tournamentId, count, options) => api.post(`/api/tournaments/${tournamentId}/players/dummy`, { count, ...options }),
  
  // Matches
  getMatches: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/matches`),
  updateMatch: (matchId, data) => api.put(`/api/tournaments/matches/${matchId}`, data),
  
  // Bracket
  generateBracket: (tournamentId) => api.post(`/api/tournaments/${tournamentId}/generate-bracket`),

  // Registration management
  reopenRegistration: (tournamentId, remarks) => api.post(`/api/tournaments/${tournamentId}/reopen-registration`, { remarks }),
  
  // Registration history
  getRegistrationHistory: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/registration-history`),
  
  // Schedule management
  generateSchedule: (tournamentId, data) => api.post(`/api/tournaments/${tournamentId}/generate-schedule`, data),
  
  // Delete schedule (soft delete)
  deleteSchedule: (tournamentId, data) => api.delete(`/api/tournaments/${tournamentId}/schedule`, { data }),
  
  // Get deleted schedules
  getDeletedSchedule: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/schedule/deleted`),
  
  // Permanently delete schedule
  permanentDeleteSchedule: (tournamentId) => api.delete(`/api/tournaments/${tournamentId}/schedule/permanent`),

  // League standings
  getStandings: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/standings`),

  // Tournament search (supports q, sport_type, status, date_from, date_to)
  search: (params) => api.get('/api/tournaments/search', { params }),

  // Analytics (organizer only)
  getAnalytics: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/analytics`),

  // Duplicate tournament (clone settings + events, no players/draws)
  duplicate: (tournamentId) => api.post(`/api/tournaments/${tournamentId}/duplicate`),

  // Registration workflow
  approvePlayer: (tournamentId, playerId) => api.patch(`/api/tournaments/${tournamentId}/players/${playerId}/approve`),
  rejectPlayer: (tournamentId, playerId) => api.patch(`/api/tournaments/${tournamentId}/players/${playerId}/reject`),
  withdrawPlayer: (tournamentId, playerId) => api.patch(`/api/tournaments/${tournamentId}/players/${playerId}/withdraw`),
};

// User / Profile API
export const userApi = {
  getProfile:    ()     => api.get('/api/auth/profile'),
  updateProfile: (data) => api.put('/api/auth/profile', data),
};

// Me / Dashboard API
export const meApi = {
  getDashboard: () => api.get('/api/auth/dashboard'),
};

// Event API — /api/tournaments/:tournamentId/events
export const eventApi = {
  list:   (tid)          => api.get(`/api/tournaments/${tid}/events`),
  create: (tid, data)    => api.post(`/api/tournaments/${tid}/events`, data),
  update: (tid, eid, d)  => api.put(`/api/tournaments/${tid}/events/${eid}`, d),
  delete: (tid, eid)     => api.delete(`/api/tournaments/${tid}/events/${eid}`),

  // Entries
  listEntries:   (tid, eid)          => api.get(`/api/tournaments/${tid}/events/${eid}/entries`),
  clearEntries:  (tid, eid)          => api.delete(`/api/tournaments/${tid}/events/${eid}/entries`),
  addEntry:      (tid, eid, data)    => api.post(`/api/tournaments/${tid}/events/${eid}/entries`, data),
  generateDummy: (tid, eid, data)    => api.post(`/api/tournaments/${tid}/events/${eid}/entries/dummy`, data),
  updateEntry:   (tid, eid, pid, d)  => api.put(`/api/tournaments/${tid}/events/${eid}/entries/${pid}`, d),
  removeEntry:      (tid, eid, pid)        => api.delete(`/api/tournaments/${tid}/events/${eid}/entries/${pid}`),
  reorderEntries:   (tid, eid, entries)    => api.patch(`/api/tournaments/${tid}/events/${eid}/entries/reorder`, { entries }),

  // Public self-registration (no auth)
  publicRegister: (tid, eid, data) => api.post(`/api/tournaments/${tid}/events/${eid}/entries/public`, data),

  // Draw
  generateDraw:       (tid, eid)       => api.post(`/api/tournaments/${tid}/events/${eid}/draw`),
  generateManualDraw: (tid, eid, data) => api.post(`/api/tournaments/${tid}/events/${eid}/manual-draw`, data),

  // Qualifying
  generateMainDrawWithQSlots: (tid, eid) => api.post(`/api/tournaments/${tid}/events/${eid}/main-draw-with-slots`),
  generateQualifyingDraw:     (tid, eid) => api.post(`/api/tournaments/${tid}/events/${eid}/qualifying-draw`),
  getQualifierSlots:          (tid, eid) => api.get(`/api/tournaments/${tid}/events/${eid}/qualifier-slots`),
  previewQualifierAssignment: (tid, eid) => api.post(`/api/tournaments/${tid}/events/${eid}/assign-qualifiers`, { dry_run: true }),
  assignQualifiers:           (tid, eid) => api.post(`/api/tournaments/${tid}/events/${eid}/assign-qualifiers`),
  promoteFromQualifying:      (tid, eid, withdrawn_player_id) => api.post(`/api/tournaments/${tid}/events/${eid}/promote-pfq`, { withdrawn_player_id }),
  promoteFromReserve:         (tid, eid, vacated_match_id, vacated_position) => api.post(`/api/tournaments/${tid}/events/${eid}/promote-pfr`, { vacated_match_id, vacated_position }),
  updatePlayerSection:        (tid, eid, pid, data) => api.patch(`/api/tournaments/${tid}/events/${eid}/entries/${pid}/section`, data),

  // Matches & standings
  getMatches:   (tid, eid)  => api.get(`/api/tournaments/${tid}/events/${eid}/matches`),
  getStandings: (tid, eid)  => api.get(`/api/tournaments/${tid}/events/${eid}/standings`),
};

// Player import — /api/tournaments/:id/players
export const playerApi = {
  previewImportEntries: (tournamentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/tournaments/${tournamentId}/players/preview-import`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importEntries: (tournamentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/tournaments/${tournamentId}/players/import-entries`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Player Profile API
export const profileApi = {
  list: (q = '', offset = 0, sport = '') => api.get('/api/profiles', { params: { q: q || undefined, offset, limit: 40, sport: sport || undefined } }),
  rankings: (filters = {}) => api.get('/api/profiles/rankings', { params: { limit: 100, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) } }),
  search: (q) => api.get('/api/profiles/search', { params: { q } }),
  getById: (profileId) => api.get(`/api/profiles/${profileId}`),
  update: (profileId, data) => api.put(`/api/profiles/${profileId}`, data),
  addIdentifier: (profileId, data) => api.post(`/api/profiles/${profileId}/identifiers`, data),
  deleteIdentifier: (profileId, identifierId) => api.delete(`/api/profiles/${profileId}/identifiers/${identifierId}`),
  merge: (keepId, mergeId) => api.post(`/api/profiles/${keepId}/merge`, { merge_profile_id: mergeId }),
};

// Staff API — /api/tournaments/:tournamentId/staff
export const staffApi = {
  getMyPermissions: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/staff/me`),
  list: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/staff`),
  invite: (tournamentId, data) => api.post(`/api/tournaments/${tournamentId}/staff/invite`, data),
  update: (tournamentId, staffId, data) => api.patch(`/api/tournaments/${tournamentId}/staff/${staffId}`, data),
  remove: (tournamentId, staffId) => api.delete(`/api/tournaments/${tournamentId}/staff/${staffId}`),
  resendInvite: (tournamentId, invitationId) => api.post(`/api/tournaments/${tournamentId}/staff/invitations/${invitationId}/resend`),
  cancelInvite: (tournamentId, invitationId) => api.delete(`/api/tournaments/${tournamentId}/staff/invitations/${invitationId}`),
  getSuggestions: (tournamentId) => api.get(`/api/tournaments/${tournamentId}/staff/suggestions`),
};

// Live Scoring API — /api/matches/:id/live
export const liveApi = {
  getLiveState: (matchId) => api.get(`/api/matches/${matchId}/live`),
  startLive: (matchId, data = {}) => api.post(`/api/matches/${matchId}/live/start`, data),
  addPoint: (matchId, player) => api.post(`/api/matches/${matchId}/live/point`, { player }),
  undoPoint: (matchId) => api.post(`/api/matches/${matchId}/live/undo`),
  nextGame: (matchId) => api.post(`/api/matches/${matchId}/live/next-game`),
  raiseChallenge: (matchId, player) => api.post(`/api/matches/${matchId}/live/challenge`, { player }),
  resolveChallengeOutcome: (matchId, outcome) => api.post(`/api/matches/${matchId}/live/challenge-result`, { outcome }),
  endLive: (matchId) => api.post(`/api/matches/${matchId}/live/end`),
};

// BAI — /api/bai
export const baiApi = {
  lookup:  (baiId) => api.get('/api/bai/lookup', { params: { id: baiId } }),
  ranking: (baiId) => api.get(`/api/bai/ranking/${baiId}`),
};

// Invite API — /api/invites
export const inviteApi = {
  preview: (token) => api.get(`/api/invites/${token}`),
  accept: (token, data) => api.post(`/api/invites/accept/${token}`, data),
};

// Admin API — /api/admin (admin role only)
export const adminApi = {
  getStats:          ()           => api.get('/api/admin/stats'),
  getUsers:          (params)     => api.get('/api/admin/users', { params }),
  updateUser:        (id, data)   => api.put(`/api/admin/users/${id}`, data),
  deleteUser:        (id)         => api.delete(`/api/admin/users/${id}`),
  getTournaments:    (params)     => api.get('/api/admin/tournaments', { params }),
  createTournament:  (data)       => api.post('/api/admin/tournaments', data),
  deleteTournament:       (id)          => api.delete(`/api/admin/tournaments/${id}`),
  setManualDrawPermission:(id, enabled) => api.patch(`/api/admin/tournaments/${id}/manual-draw-permission`, { enabled }),
  getPlayers:        (params)     => api.get('/api/admin/players', { params }),
  linkPlayer:        (id, email)  => api.put(`/api/admin/players/${id}/link`, { email }),
  unlinkPlayer:      (id)         => api.delete(`/api/admin/players/${id}/link`),
  getAuditLogs:      (params)     => api.get('/api/admin/audit-logs', { params }),
  getOrganizers:     ()           => api.get('/api/admin/organizers'),
  getDesktopLicenses:  (params)        => api.get('/api/admin/desktop-licenses', { params }),
  grantDesktopLicense: (userId, data)  => api.post(`/api/admin/desktop-licenses/${userId}/grant`, data),
  revokeDesktopLicense:(userId)        => api.delete(`/api/admin/desktop-licenses/${userId}/revoke`),
  updateLicenseExpiry: (userId, data)  => api.put(`/api/admin/desktop-licenses/${userId}/expiry`, data),
  getDevicesForUser:   (userId)        => api.get(`/api/admin/desktop-licenses/${userId}/devices`),
  revokeDevice:        (userId, devId) => api.delete(`/api/admin/desktop-licenses/${userId}/devices/${devId}`),
};

// Club & Court API
export const clubApi = {
  getAll: () => api.get('/api/clubs'),
  getById: (id) => api.get(`/api/clubs/${id}`),
  create: (data) => api.post('/api/clubs', data),
  update: (id, data) => api.put(`/api/clubs/${id}`, data),
  delete: (id) => api.delete(`/api/clubs/${id}`),
  getCourts: (clubId) => api.get(`/api/clubs/${clubId}/courts`),
  createCourt: (clubId, data) => api.post(`/api/clubs/${clubId}/courts`, data),
  updateCourt: (clubId, courtId, data) => api.put(`/api/clubs/${clubId}/courts/${courtId}`, data),
  deleteCourt: (clubId, courtId) => api.delete(`/api/clubs/${clubId}/courts/${courtId}`),
};

// Player Dashboard API
export const playerDashboardApi = {
  getMyProfile:      ()             => api.get('/api/player/my-profile'),
  getMyTournaments:  ()             => api.get('/api/player/my-tournaments'),
  getDashboardStats: ()             => api.get('/api/player/dashboard-stats'),
  getMyEntries:      (tournamentId) => api.get(`/api/player/my-entries/${tournamentId}`),
};

